import styled from 'styled-components'
import { SubHeader } from '../GvSubHeader'
import { OverviewSpecification } from './OverviewSpecification'
import { useEffect, useRef, useState } from 'react'
import { TextId, useTextsV2 } from '../../../utils/useTexts'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  getMarketLanguageState,
  getMarketSettingsState,
} from '../../../store/appSlice'
import {
  getPreloaderState,
  getSessionState,
  pushPageViewTrackingEvent,
  startHideInitialLoadingScreen,
} from '../../../store/sessionDataSlice'
import { getTruckInfoState } from '../../../store/truckInfoSlice'
import { MainImageArea } from '../MainImage/MainImageArea'
import { BreakpointWidthPx } from '../../../css/BreakpointWidthPx'
import { PortraitFooter } from '../../../components/Footer/PortraitFooter'
import { CustomScrollbarMixin } from '../../../css/CustomScrollbarMixin'
import { NewsBanner } from '../../../components/NewsBanner/NewsBanner'
import { BreakpointHeightPx } from '../../../css/BreakpointHeightPx'
import { ScaniaAdobeTrackingPageName } from '../../../utils/adobeAnalytics'
import { useResizeObserver } from '../../../utils/useResizeObserver'
import { Size2d } from '../../../types/Size2d'
import { getTruckImageState } from '../../../store/truckImageSlice'
import { usePageUpdate } from '../../../utils/useBMPageUpdate'
import { GuidedViewPageType } from '../../../store/types'
import { MainImageControlPanelMobile } from '../MainImage/MainImageControlPanelMobile'

interface OverviewPageRootProps {
  $pageIsReady: boolean
}

const OverviewPageRoot = styled.main<OverviewPageRootProps>`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  background-color: white;
  visibility: ${(props) => (props.$pageIsReady ? '' : 'hidden')};

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    overflow-y: auto;
    overflow-x: hidden;
    ${CustomScrollbarMixin}
  }
`

const MainArea = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  box-sizing: border-box;
  padding: 24px 24px 16px 24px;

  @media screen and (max-width: ${BreakpointWidthPx.XLarge}px) {
    padding: 0px 24px 16px 24px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    padding: 0px 16px 8px 32px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    flex: none;
    padding: 20px 16px 8px 20px;
  }
`

const HeaderSection = styled.section`
  width: 100%;
  padding-left: 60px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    padding-left: 0px;
  }
`

const Header = styled.h1`
  font-size: 60px;

  @media screen and (max-width: ${BreakpointWidthPx.XLarge}px) {
    font-size: 48px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    font-size: 32px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    font-size: min(3rem, 8vw, 5vh);
    white-space: normal;
    word-break: keep-all;
  }
`

const Text = styled.div`
  width: 67%;
  margin-top: 16px;
  font-size: 16px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    width: 100%;
  }
`

const ContentSection = styled.section`
  display: flex;
  flex: 1;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    flex-direction: column;
    flex: none;
  }
`

const ImageSection = styled.section`
  display: flex;
  flex-direction: column;
  flex: 1;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    height: 280px;
    width: 100%;
    flex: unset;
    padding-bottom: 16px;
  }
`

const SummarySection = styled.section`
  height: 100%;
  padding-bottom: 80px;
  flex: 0 0 33%;
  max-width: 500px;
  padding-bottom: 0px;
  overflow: hidden;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    width: 100%;
    height: unset;
    flex: none;
    max-width: none;
  }
`

export interface OverviewPageProps {
  handleNewClick: () => void
  handleRfqOrEysClick: () => void
  navigateToMenu: (
    id: string,
    menuPath: string,
    gvPageToJumpTo: GuidedViewPageType,
  ) => void
  handleSessionFailure: () => void
  handleFatalError: () => void
}

export function OverviewPage({
  handleNewClick,
  handleRfqOrEysClick,
  navigateToMenu,
  handleSessionFailure,
  handleFatalError,
}: OverviewPageProps): JSX.Element {
  const dispatch = useAppDispatch()
  const t2 = useTextsV2()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const size = useResizeObserver<Size2d>(rootRef, { width: 0, height: 0 })
  const truckInfo = useAppSelector(getTruckInfoState)
  const marketSettings = useAppSelector(getMarketSettingsState)
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const preloaderState = useAppSelector(getPreloaderState)
  const { showNewsBanner } = useAppSelector(getSessionState)
  const imageUrl = useAppSelector(getTruckImageState).imageUrl
  const [pageReady, setPageReady] = useState<boolean>(false)

  useEffect(() => {
    if (truckInfo && imageUrl) {
      setPageReady(true)
    }
  }, [truckInfo, imageUrl])

  useEffect(() => {
    if (!preloaderState.preloaderIsHidden) {
      dispatch(startHideInitialLoadingScreen())
    }
  }, [dispatch, preloaderState.preloaderIsHidden])

  // Adobe user tracking.
  useEffect(() => {
    if (!marketLanguage) {
      return
    }
    dispatch(
      pushPageViewTrackingEvent({
        pageName: ScaniaAdobeTrackingPageName.GvOverview,
        marketLanguage,
      }),
    )
  }, [dispatch, marketLanguage])

  usePageUpdate(handleFatalError, handleSessionFailure)

  return (
    <OverviewPageRoot
      data-name="GuidedViewOverview"
      ref={rootRef}
      $pageIsReady={pageReady}
    >
      <SubHeader
        handleNewClick={handleNewClick}
        handleSessionFailure={handleSessionFailure}
        navigateToMenu={navigateToMenu}
      />
      <MainArea>
        <HeaderSection>
          <Header>
            {t2(TextId.HEADER_GV_CURRENT_CONFIGURATION).toUpperCase()}
          </Header>
          <Text>{t2(TextId.DESCRIPTION_OVERVIEW)}</Text>
        </HeaderSection>
        <ContentSection>
          <ImageSection>
            <MainImageArea
              showImageControlPanel={true}
              showFullViewIcon={true}
              showSideArrows={true}
              navigateToMenu={navigateToMenu}
            />
            <MainImageControlPanelMobile showIcons={true} />
          </ImageSection>
          <SummarySection>
            <OverviewSpecification
              handleRfqOrEysClick={handleRfqOrEysClick}
              parentSize={size}
            />
          </SummarySection>
        </ContentSection>
      </MainArea>
      <PortraitFooter
        displayAtScreenMaxWidth={BreakpointWidthPx.Small}
        displayAtScreenMaxHeight={BreakpointHeightPx.Small}
      />
      {marketSettings?.newsBannerEnabled && showNewsBanner && <NewsBanner />}
    </OverviewPageRoot>
  )
}
